import { defineStore, storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'
import {
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  documentId,
  getDoc,
  increment,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
  where,
  type Unsubscribe
} from 'firebase/firestore'
import { useCollection } from 'vuefire'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { firestore } from '@/fb'
import { debug, warn } from '@/logger'
import type { Pantry, PantryItem, PantryMember, ShoppingListItem } from '@/models/firestore'
import { pantryConverter } from '@/models/firestoreConverters'
import { useUserStore } from './userStore'

export type ScannedPantryItem = {
  name: string
  quantity?: string
  unit?: string
}

export type ScanPantryPhotoResponse = {
  items?: ScannedPantryItem[]
  confidence?: 'high' | 'medium' | 'low'
  source?: string
}

export type ReceiptScanItem = {
  name: string
  rawName?: string
  quantity?: number
  price?: number
  unit?: string
  category?: string
}

export type ScanReceiptResponse = {
  store?: string
  date?: string
  total?: number
  confidence?: 'high' | 'medium' | 'low'
  items?: ReceiptScanItem[]
  source?: string
}

// Legacy data structure for backward compatibility (used by data.ts facade)
export interface Pantries {
  docs: string[]
  [key: string]: Pantry | string[]
}

export const usePantryStore = defineStore('pantry', () => {
  const userStore = useUserStore()
  const { user, currentPantry } = storeToRefs(userStore)

  // ==========================================
  // VueFire Reactive Collection Binding (Lazy)
  // ==========================================

  // Computed query source - returns null when no pantries to query (lazy loading)
  const pantriesQuerySource = computed(() => {
    const pantryIds = user.value?.pantries
    if (!pantryIds || pantryIds.length === 0) return null

    // Firestore 'in' queries limited to 30 items
    // If user has more than 30 pantries, we'd need to batch (unlikely)
    return query(
      collection(firestore, 'pantries'),
      where(documentId(), 'in', pantryIds.slice(0, 30))
    )
  })

  // VueFire reactive binding for pantries
  const {
    data: pantriesData,
    pending: pantriesLoading,
    error: pantriesError
  } = useCollection<Pantry>(pantriesQuerySource, { ssrKey: 'pantries' })

  // Extended pantry data with members and items (populated by subcollection listeners)
  const pantryExtensions = ref<Map<string, { members: PantryMember[], pantryItems: PantryItem[] }>>(new Map())

  // Merge VueFire data with extensions
  const pantriesArray = computed<Pantry[]>(() => {
    if (!pantriesData.value) return []
    return pantriesData.value.map(p => {
      const ext = pantryExtensions.value.get(p.id!)
      return {
        ...p,
        id: p.id,
        members: ext?.members ?? p.members ?? [],
        pantryItems: ext?.pantryItems ?? []
      }
    })
  })

  // State for editing
  const pantryItemBeingEdited = ref<PantryItem | Record<string, never>>({})

  // Listener tracking for subcollections
  const pantryMembersUnsubscribes = ref<Map<string, Unsubscribe>>(new Map())
  const pantryItemsUnsubscribes = ref<Map<string, Unsubscribe>>(new Map())

  // ==========================================
  // Subcollection Binding (triggered by pantry list changes)
  // ==========================================

  // Watch for pantry list changes and bind subcollections
  watch(
    pantriesData,
    (newPantries) => {
      if (!newPantries) return

      const currentIds = new Set(newPantries.map(p => p.id!))

      // Subscribe to subcollections for new pantries
      for (const pantry of newPantries) {
        if (pantry.id && !pantryMembersUnsubscribes.value.has(pantry.id)) {
          bindPantryMembers(pantry.id)
          bindPantryItems(pantry.id)
        }
      }

      // Unsubscribe from removed pantries
      for (const [id, unsub] of pantryMembersUnsubscribes.value) {
        if (!currentIds.has(id)) {
          unsub()
          pantryMembersUnsubscribes.value.delete(id)
        }
      }
      for (const [id, unsub] of pantryItemsUnsubscribes.value) {
        if (!currentIds.has(id)) {
          unsub()
          pantryItemsUnsubscribes.value.delete(id)
        }
      }
    },
    { immediate: true }
  )

  // Helper to find pantry by ID
  const findPantry = (id: string): Pantry | undefined => {
    return pantriesArray.value.find(p => p.id === id)
  }

  const findPantryIndex = (id: string): number => {
    return pantriesArray.value.findIndex(p => p.id === id)
  }

  // Legacy getter for backward compatibility with data.ts facade
  const pantries = computed((): Pantries => {
    const result: Pantries = { docs: [] }
    for (const pantry of pantriesArray.value) {
      if (pantry.id) {
        result.docs.push(pantry.id)
        result[pantry.id] = pantry
      }
    }
    return result
  })

  // Simple array getters
  const getPantries = computed(() => pantriesArray.value)

  const getPantriesDocs = computed((): string[] => {
    return pantriesArray.value.map(p => p.id!).filter(Boolean)
  })

  const tasks = computed((): any[] => {
    if (!user.value) return []

    const allTasks: any[] = []
    for (const pantry of pantriesArray.value) {
      if (!pantry.pantryItems) continue

      for (const item of pantry.pantryItems) {
        if (item.status === 'need') {
          allTasks.push({
            type: 'pantry_item',
            status: 'need',
            name: item.displayName ?? item.name ?? '',
            quantity: item.quantity ?? 0,
            measurement: item.measurement ?? '',
            pantryId: pantry.id,
            pantryName: pantry.name ?? '',
            id: item.id
          })
        }
      }
    }

    return allTasks
  })

  const getTopActiveItems = computed(() => {
    const allItems: { item: PantryItem, pantryId: string }[] = []

    for (const pantry of pantriesArray.value) {
      if (!pantry.pantryItems) continue
      for (const item of pantry.pantryItems) {
        if (item.statusChangeCount && item.statusChangeCount > 0) {
          allItems.push({ item, pantryId: pantry.id! })
        }
      }
    }

    return allItems
      .sort((a, b) => (b.item.statusChangeCount || 0) - (a.item.statusChangeCount || 0))
      .slice(0, 10)
  })

  const getPantry = (id: string): Pantry | null => {
    return findPantry(id) ?? null
  }

  const getPantryForMutation = (id: string): { members: PantryMember[], pantryItems: PantryItem[] } | undefined => {
    // Get the mutable extension object for this pantry
    if (!pantryExtensions.value.has(id)) {
      pantryExtensions.value.set(id, { members: [], pantryItems: [] })
    }
    return pantryExtensions.value.get(id)
  }

  const getPantryMembers = (id: string): PantryMember[] => {
    const pantry = getPantry(id)
    return pantry?.members ?? []
  }

  const areMultiplePantries = computed((): boolean => {
    return pantriesArray.value.length > 1
  })

  const pantryPantryItems = (id: string): PantryItem[] => {
    const pantry = getPantry(id)
    return pantry?.pantryItems ?? []
  }

  const getPantryItemBeingEdited = computed(() => pantryItemBeingEdited.value)

  // User shopping list now comes from user profile (persisted in Firestore)
  const getUserShoppingList = computed<ShoppingListItem[]>(() => {
    return user.value?.shopping_list ?? []
  })

  const getPantryShoppingList = (id: string): PantryItem[] => {
    const pantry = getPantry(id)
    if (!pantry?.pantryItems) return []
    // Shopping list includes need_more and out_of_stock, but NOT not_tracking
    return pantry.pantryItems.filter(item =>
      item.status === 'need' ||
      item.status === 'need_more' ||
      item.status === 'out_of_stock'
    )
  }

  const getIngredientIsInCurrentPantry = (ingredient: PantryItem) => {
    const pantryId = currentPantry.value
    if (!pantryId) {
      return { status: 'not found', pantryItem: null }
    }
    const items = pantryPantryItems(pantryId)

    const ingredientNameRaw = (ingredient.displayName ?? ingredient.name ?? '').toString()
    const ingredientName = ingredientNameRaw.trim().toLowerCase()
    if (!ingredientName) {
      return { status: 'not found', pantryItem: null }
    }

    for (const pantryItem of items) {
      const pantryItemNameRaw = (pantryItem.displayName ?? pantryItem.name ?? '').toString()
      const pantryItemName = pantryItemNameRaw.trim().toLowerCase()

      const nameMatch = pantryItemName && pantryItemName === ingredientName
      const aliasMatch = (pantryItem.aliases ?? []).some((alias) => {
        const aliasName = (alias?.name ?? '').toString().trim().toLowerCase()
        return aliasName && aliasName === ingredientName
      })

      const match = nameMatch || aliasMatch

      if (match) {
        if (pantryItem.status === 'not_tracking') {
          return { status: 'not tracked', pantryItem }
        }
        let status = 'not found'
        if (pantryItem.formComplexity === 0) {
          if (pantryItem.status === 'in_stock') status = 'full'
          else if (pantryItem.status === 'need_more') status = 'partial'
          else status = 'not in stock'
        } else if (pantryItem.formComplexity === 1) {
          if (pantryItem.measurement === ingredient.measurement) {
            status = Number(pantryItem.quantity) >= Number(ingredient.quantity ?? 0) ? 'full' : 'partial'
          } else {
            status = 'partial'
          }
        }

        return { status, pantryItem }
      }
    }

    return { status: 'not found', pantryItem: null }
  }

  // Unbind helpers for subcollections
  const unbindPantryMembersListener = (id: string) => {
    const unsub = pantryMembersUnsubscribes.value.get(id)
    if (unsub) {
      unsub()
      pantryMembersUnsubscribes.value.delete(id)
    }
  }

  const unbindPantryItemsListener = (id: string) => {
    const unsub = pantryItemsUnsubscribes.value.get(id)
    if (unsub) {
      unsub()
      pantryItemsUnsubscribes.value.delete(id)
    }
  }

  const unbindAllSubcollections = () => {
    pantryMembersUnsubscribes.value.forEach((unsub) => unsub())
    pantryMembersUnsubscribes.value.clear()
    pantryItemsUnsubscribes.value.forEach((unsub) => unsub())
    pantryItemsUnsubscribes.value.clear()
    pantryExtensions.value.clear()
  }

  // Actions
  const setPantryItemBeingEdited = (pantryItem: PantryItem) => {
    pantryItemBeingEdited.value = { ...pantryItem }
  }

  const clearPantryItemBeingEdited = () => {
    pantryItemBeingEdited.value = {}
  }

  const bindPantryMembers = async (id: string) => {
    if (!id) return

    if (pantryMembersUnsubscribes.value.has(id)) {
      debug('bindPantryMembers: Already bound for pantry ' + id)
      return
    }

    try {
      const membersRef = collection(firestore, 'pantries', id, 'members')

      const unsub = onSnapshot(membersRef, async (snapshot) => {
        const members: PantryMember[] = []

        for (const memberDoc of snapshot.docs) {
          const memberData = memberDoc.data() as any
          if (memberData.deleted) continue

          const memberUid = memberData.userId || memberData.ref?.id || memberDoc.id
          if (typeof memberUid === 'string') {
            try {
              const userDocRef = doc(firestore, 'public_profiles', memberUid)
              const userDocSnapshot = await getDoc(userDocRef)
              if (userDocSnapshot.exists()) {
                const profile = userDocSnapshot.data() as Record<string, any>
                members.push({
                  id: memberDoc.id,
                  name: profile?.name ?? profile?.displayName ?? 'Unknown User',
                  image_url: profile?.image_url ?? null,
                  email: memberData?.email ?? null,
                  role: memberData?.role ?? 'member'
                })
                continue
              }
            } catch (e) {
              // Fall through
            }
          }
          members.push({
            id: memberDoc.id,
            name: memberData?.name ?? 'Unknown User',
            image_url: memberData?.image_url ?? null,
            email: memberData?.email ?? null,
            role: memberData?.role ?? 'member'
          })
        }

        const pantry = getPantryForMutation(id)
        if (pantry) pantry.members = members
      }, (error) => {
        warn('bindPantryMembers: Error in snapshot: ' + error.message)
        const pantry = getPantryForMutation(id)
        if (pantry) pantry.members = []
      })

      pantryMembersUnsubscribes.value.set(id, unsub)
    } catch (error) {
      warn('bindPantryMembers: Error setting up listener: ' + (error as Error).message)
      const pantry = getPantryForMutation(id)
      if (pantry) pantry.members = []
    }
  }

  const bindPantryItems = async (id: string) => {
    if (!id) return

    if (pantryItemsUnsubscribes.value.has(id)) {
      debug('bindPantryItems: Already bound for pantry ' + id)
      return
    }

    try {
      const itemsRef = collection(firestore, 'pantries', id, 'pantry_items')

      const unsub = onSnapshot(itemsRef, (snapshot) => {
        const items: PantryItem[] = snapshot.docs.map(itemDoc => ({
          id: itemDoc.id,
          ...(itemDoc.data() as PantryItem)
        }))

        const pantry = getPantryForMutation(id)
        if (pantry) {
          pantry.pantryItems = items
        }
      }, (error) => {
        warn('bindPantryItems: Error in snapshot: ' + error.message)
      })

      pantryItemsUnsubscribes.value.set(id, unsub)
    } catch (error) {
      warn('bindPantryItems: Error setting up listener: ' + (error as Error).message)
    }
  }

  const addUserToPantry = async ({ userId, pantryId }: { userId: string; pantryId: string }) => {
    if (!userId || !pantryId) return

    try {
      const addMember = httpsCallable<
        { userId: string; pantryId: string },
        void
      >(getFunctions(), 'addUserToPantry')
      await addMember({ userId, pantryId })
    } catch (error) {
      warn('addUserToPantry: Error', error)
    }
  }

  const removeUserFromPantry = async ({ userId, pantryId }: { userId: string; pantryId: string }) => {
    if (!userId || !pantryId) return
    try {
      const removeMember = httpsCallable<
        { userId: string; pantryId: string },
        void
      >(getFunctions(), 'removeUserFromPantry')
      await removeMember({ userId, pantryId })
    } catch (error) {
      warn('removeUserFromPantry: Error removing pantry membership', error)
    }
  }

  const removePantryItemFromPantry = async ({ pantryItemId, pantryId }: { pantryItemId: string; pantryId: string }) => {
    if (!pantryItemId || !pantryId) return

    try {
      const itemRef = doc(firestore, 'pantries', pantryId, 'pantry_items', pantryItemId)
      await deleteDoc(itemRef)

      // Note: The onSnapshot listener in bindPantryItems will automatically
      // update pantryItems when the document is deleted from Firestore

      if (pantryItemBeingEdited.value && (pantryItemBeingEdited.value as PantryItem).id === pantryItemId) {
        clearPantryItemBeingEdited()
      }
    } catch (error) {
      warn('removePantryItemFromPantry: Error', error)
    }
  }

  const setPantryItemStatusToInStock = async ({ pantryItem, pantryId }: { pantryItem: Pick<PantryItem, 'id'>; pantryId: string }) => {
    if (!pantryItem?.id || !pantryId) return

    try {
      const itemRef = doc(firestore, 'pantries', pantryId, 'pantry_items', pantryItem.id)
      await updateDoc(itemRef, {
        status: 'in_stock',
        statusChangeCount: increment(1)
      })

      const pantry = getPantryForMutation(pantryId)
      if (pantry?.pantryItems?.[pantryItem.id]) {
        pantry.pantryItems[pantryItem.id].status = 'in_stock'
        pantry.pantryItems[pantryItem.id].statusChangeCount = (pantry.pantryItems[pantryItem.id].statusChangeCount || 0) + 1
      }
    } catch (error) {
      warn('setPantryItemStatusToInStock: Error', error)
    }
  }

  const updatePantryItemQuantity = async ({ pantryItem, quantity, pantryId }: { pantryItem: Pick<PantryItem, 'id'>; quantity: number; pantryId: string }) => {
    if (!pantryItem?.id || !pantryId) return

    try {
      const parsedQuantity = parseFloat(quantity.toString())
      const itemRef = doc(firestore, 'pantries', pantryId, 'pantry_items', pantryItem.id)
      await updateDoc(itemRef, { quantity: parsedQuantity })

      const pantry = getPantryForMutation(pantryId)
      if (pantry?.pantryItems?.[pantryItem.id]) {
        pantry.pantryItems[pantryItem.id].quantity = parsedQuantity
      }
    } catch (error) {
      warn('updatePantryItemQuantity: Error', error)
    }
  }

  const setPantryItemStatusToOutOfStock = async ({ pantryItem, pantryId }: { pantryItem: Pick<PantryItem, 'id'>; pantryId: string }) => {
    if (!pantryItem?.id || !pantryId) return

    try {
      const itemRef = doc(firestore, 'pantries', pantryId, 'pantry_items', pantryItem.id)
      await updateDoc(itemRef, {
        status: 'out_of_stock',
        statusChangeCount: increment(1)
      })

      const pantry = getPantryForMutation(pantryId)
      if (pantry?.pantryItems?.[pantryItem.id]) {
        pantry.pantryItems[pantryItem.id].status = 'out_of_stock'
        pantry.pantryItems[pantryItem.id].statusChangeCount = (pantry.pantryItems[pantryItem.id].statusChangeCount || 0) + 1
      }
    } catch (error) {
      warn('setPantryItemStatusToOutOfStock: Error', error)
    }
  }

  const addItemToPantry = async ({ item, pantryId }: { item: Partial<PantryItem> & { name: string }; pantryId: string }): Promise<string | undefined> => {
    if (!item?.name || !pantryId) {
      warn('addItemToPantry: Name and pantryId are required')
      return
    }



    try {
      const pantryRef = doc(firestore, 'pantries', pantryId)
      const itemsRef = collection(pantryRef, 'pantry_items')
      const newItemRef = doc(itemsRef)

      const itemToAdd: Partial<PantryItem> = {
        name: item.name,
        displayName: item.name,
        aliases: item.aliases || [{ name: item.name }],
        status: item.status || 'in_stock',
        formComplexity: item.formComplexity || 0
      }

      if ((item as any).category) itemToAdd.category = (item as any).category
      if (item.measurement) itemToAdd.measurement = item.measurement
      if (item.quantity !== undefined) itemToAdd.quantity = typeof item.quantity === 'string' ? parseFloat(item.quantity) : item.quantity

      await setDoc(newItemRef, itemToAdd)
      return newItemRef.id
    } catch (error) {
      warn('addItemToPantry: Error', error)
      throw error
    }
  }

  const addItemToCurrentPantry = async ({ item }: { item: Partial<PantryItem> & { name: string } }) => {
    const pantryId = currentPantry.value
    if (!pantryId) {
      warn('addItemToCurrentPantry: No current pantry set')
      return
    }
    return await addItemToPantry({ item, pantryId })
  }

  const cyclePantryItemStatus = async ({ pantryItem, pantryId }: { pantryItem: Pick<PantryItem, 'id' | 'status'>; pantryId: string }) => {
    if (!pantryItem?.id || !pantryId) return
    // Cycle: in_stock -> need_more -> out_of_stock -> not_tracking -> in_stock
    const statusCycle: Array<PantryItem['status']> = ['in_stock', 'need_more', 'out_of_stock', 'not_tracking']
    const currentStatus = pantryItem.status ?? 'out_of_stock'
    const currentIndex = statusCycle.indexOf(currentStatus)
    const nextStatus = statusCycle[(currentIndex + 1) % statusCycle.length]

    try {
      const itemRef = doc(firestore, 'pantries', pantryId, 'pantry_items', pantryItem.id)
      await updateDoc(itemRef, {
        status: nextStatus,
        updated_at: new Date(),
        statusChangeCount: increment(1)
      })

      const pantry = getPantryForMutation(pantryId)
      if (pantry?.pantryItems?.[pantryItem.id]) {
        pantry.pantryItems[pantryItem.id].status = nextStatus
        pantry.pantryItems[pantryItem.id].statusChangeCount = (pantry.pantryItems[pantryItem.id].statusChangeCount || 0) + 1
      }
    } catch (error) {
      warn('cyclePantryItemStatus: Error', error)
    }
  }

  const setPantryItemStatus = async ({ pantryItem, pantryId, status }: { pantryItem: Pick<PantryItem, 'id'>; pantryId: string; status: string; }) => {
    if (!pantryItem?.id || !pantryId) return

    try {
      const itemRef = doc(firestore, 'pantries', pantryId, 'pantry_items', pantryItem.id)
      await updateDoc(itemRef, {
        status,
        updated_at: new Date(),
        statusChangeCount: increment(1)
      })

      const pantry = getPantryForMutation(pantryId)
      if (pantry?.pantryItems?.[pantryItem.id]) {
        pantry.pantryItems[pantryItem.id].status = status
        pantry.pantryItems[pantryItem.id].statusChangeCount = (pantry.pantryItems[pantryItem.id].statusChangeCount || 0) + 1
      }
    } catch (error) {
      warn('setPantryItemStatus: Error', error)
    }
  }

  const setPantryItemCategory = async ({ pantryItemId, pantryId, category }: { pantryItemId: string; pantryId: string; category: string | null }) => {
    if (!pantryItemId || !pantryId) return

    try {
      const itemRef = doc(firestore, 'pantries', pantryId, 'pantry_items', pantryItemId)
      await updateDoc(itemRef, { category: category || null, updated_at: new Date() })

      const pantry = getPantryForMutation(pantryId)
      if (pantry?.pantryItems?.[pantryItemId]) {
        pantry.pantryItems[pantryItemId].category = category || undefined
      }
    } catch (error) {
      warn('setPantryItemCategory: Error', error)
    }
  }

  const savePantryItemBeingEdited = async (pantryId: string) => {
    if (!pantryId) return

    const item = pantryItemBeingEdited.value as PantryItem
    if (!item || !item.name) {
      warn('savePantryItemBeingEdited: No item to save')
      return
    }

    try {
      const pantryRef = doc(firestore, 'pantries', pantryId)
      const itemsRef = collection(pantryRef, 'pantry_items')
      // Sanitize fields before saving to Firestore
      const itemData: any = {
        ...item,
        // Ensure aliases are plain objects with only the name field
        aliases: (item.aliases || []).map(a => ({ name: (a as any).name })),
        // Normalize category to undefined when empty string
        category: item.category || undefined
      }
      if (itemData.quantity !== undefined) {
        itemData.quantity = parseFloat(itemData.quantity.toString())
        if (Number.isNaN(itemData.quantity)) delete itemData.quantity
      }
      // Strip undefined fields (Firestore rejects undefined)
      Object.keys(itemData).forEach((key) => {
        if (itemData[key] === undefined) {
          delete itemData[key]
        }
      })
      delete itemData.id

      // Always set updated_at timestamp
      itemData.updated_at = new Date()

      if (item.id) {
        const itemRef = doc(itemsRef, item.id)
        await setDoc(itemRef, itemData, { merge: true })
      } else {
        // Set created_at for new items
        itemData.created_at = new Date()
        const newItemRef = doc(itemsRef)
        await setDoc(newItemRef, itemData)
      }

      clearPantryItemBeingEdited()
    } catch (error) {
      warn(`savePantryItemBeingEdited: Error for pantry ${pantryId}: ${String(error)}`)
    }
  }

  const addIngredientAsPantryItemAlias = async ({ ingredient, ingredientName, targetPantryItem, pantryId }: { ingredient?: any; ingredientName?: string; targetPantryItem: PantryItem; pantryId: string; pantryItemId?: string }) => {
    if (!pantryId || !targetPantryItem?.id) return

    try {
      // Validate input and normalize alias data (Firestore does not accept undefined fields)
      const aliasName = (ingredient?.name || ingredientName || '').toString().trim()
      if (!aliasName) throw new Error('Ingredient name is required to create an alias')

      // Create sanitized alias objects (only include `name` field)
      const newAlias = { name: aliasName }

      const itemRef = doc(firestore, 'pantries', pantryId, 'pantry_items', targetPantryItem.id)

      // Build deduped, normalized aliases list
      const merged = [...(targetPantryItem.aliases || []), newAlias]
      const updatedAliases = merged.reduce<Record<string, { name: string }>>((acc, a) => {
        const n = (a && (a as any).name) ? (a as any).name.toString().trim() : ''
        if (!n) return acc
        const key = n.toLowerCase()
        if (!acc[key]) acc[key] = { name: n }
        return acc
      }, {})

      const aliasesArray = Object.values(updatedAliases)

      await updateDoc(itemRef, { aliases: aliasesArray })

      const pantry = getPantryForMutation(pantryId)
      if (pantry?.pantryItems?.[targetPantryItem.id]) {
        pantry.pantryItems[targetPantryItem.id].aliases = aliasesArray
      }
    } catch (error) {
      warn('addIngredientAsPantryItemAlias: Error', error)
      // Re-throw so callers can handle/display errors appropriately
      throw error
    }
  }

  const mergePantryItems = async ({ pantryItem, targetPantryItem, pantryId }: { pantryItem: PantryItem; targetPantryItem: PantryItem; pantryId: string }) => {
    if (!pantryItem?.id || !targetPantryItem?.id || !pantryId) return

    try {
      // Merge aliases and normalize (remove undefined fields and dedupe)
      const merged = [...(targetPantryItem.aliases || []), ...(pantryItem.aliases || [])]
      const normalized = merged.reduce<Record<string, { name: string }>>((acc, a) => {
        const n = (a && (a as any).name) ? (a as any).name.toString().trim() : ''
        if (!n) return acc
        const key = n.toLowerCase()
        if (!acc[key]) acc[key] = { name: n }
        return acc
      }, {})

      const mergedAliases = Object.values(normalized)

      const targetRef = doc(firestore, 'pantries', pantryId, 'pantry_items', targetPantryItem.id)
      await updateDoc(targetRef, { aliases: mergedAliases })

      const pantry = getPantryForMutation(pantryId)
      if (pantry?.pantryItems?.[targetPantryItem.id]) {
        pantry.pantryItems[targetPantryItem.id].aliases = mergedAliases
      }

      await removePantryItemFromPantry({ pantryItemId: pantryItem.id, pantryId })
    } catch (error) {
      warn('mergePantryItems: Error', error)
    }
  }

  const upsertPantry = async ({ pantryId, name }: { pantryId?: string | null; name: string }) => {
    if (!name) throw new Error('Pantry name is required')

    const pantriesRef = collection(firestore, 'pantries').withConverter(pantryConverter)
    let pantryRef = pantryId ? doc(pantriesRef, pantryId) : doc(pantriesRef)

    const payload: Pantry = {
      name
    }

    await setDoc(pantryRef, payload, { merge: true })

    if (user.value?.uid) {
      await addUserToPantry({ userId: user.value.uid, pantryId: pantryRef.id })
    }

    return pantryRef.id
  }

  // ==========================================
  // USER SHOPPING LIST (persisted to Firestore)
  // ==========================================
  const addItemToUserShoppingList = async (item: ShoppingListItem) => {
    // Coerce name to string to avoid accidental objects being stored
    const rawName = item?.name
    const name = rawName === undefined || rawName === null ? '' : String(rawName).trim()
    if (!name || !user.value?.uid) return

    const currentList = user.value?.shopping_list ?? []
    const exists = currentList.some(
      (existing) => String(existing.name).toLowerCase() === name.toLowerCase()
    )
    if (exists) return

    const newItem: ShoppingListItem = {
      name,
      checked: false,
      ...(item.measurements && { measurements: item.measurements })
    }

    try {
      const userRef = doc(firestore, 'users', user.value.uid)
      await updateDoc(userRef, {
        shopping_list: [...currentList, newItem]
      })
    } catch (error) {
      warn('Failed to add item to shopping list:', error)
    }
  }

  const removeItemsFromUserShoppingList = async (selectedItems: Record<string, boolean>) => {
    if (!user.value?.uid) return

    const currentList = user.value?.shopping_list ?? []
    const newList = currentList.filter((item) => !selectedItems[item.name])

    try {
      const userRef = doc(firestore, 'users', user.value.uid)
      await updateDoc(userRef, {
        shopping_list: newList
      })
    } catch (error) {
      warn('Failed to remove items from shopping list:', error)
    }
  }

  const toggleShoppingListItemChecked = async (itemName: string) => {
    if (!user.value?.uid) return

    const currentList = user.value?.shopping_list ?? []
    const newList = currentList.map((item) => {
      if (item.name === itemName) {
        return { ...item, checked: !item.checked }
      }
      return item
    })

    try {
      const userRef = doc(firestore, 'users', user.value.uid)
      await updateDoc(userRef, {
        shopping_list: newList
      })
    } catch (error) {
      warn('Failed to toggle shopping list item:', error)
    }
  }

  const clearCheckedShoppingListItems = async () => {
    if (!user.value?.uid) return

    const currentList = user.value?.shopping_list ?? []
    const newList = currentList.filter((item) => !item.checked)

    try {
      const userRef = doc(firestore, 'users', user.value.uid)
      await updateDoc(userRef, {
        shopping_list: newList
      })
    } catch (error) {
      warn('Failed to clear checked items:', error)
    }
  }

  const unbindAllPantryData = () => {
    unbindAllSubcollections()
  }

  // ==========================================
  // AI: PANTRY PHOTO SCAN (via Cloud Function)
  // ==========================================
  const scanPantryPhoto = async ({ imagesBase64, visionModel }: { imagesBase64: string[]; visionModel?: string }): Promise<ScanPantryPhotoResponse> => {
    if (!imagesBase64?.length) {
      throw new Error('At least one image is required')
    }

    const functions = getFunctions()
    const scanFn = httpsCallable(functions, 'scanPantryPhoto')
    const resp = await scanFn({ images: imagesBase64, ...(visionModel ? { visionModel } : {}) })
    return resp.data as ScanPantryPhotoResponse
  }

  const scanReceipt = async ({ imagesBase64, visionModel }: { imagesBase64: string[]; visionModel?: string }): Promise<ScanReceiptResponse> => {
    if (!imagesBase64?.length) {
      throw new Error('At least one image is required')
    }

    const functions = getFunctions()
    const scanFn = httpsCallable(functions, 'scanReceipt')
    const resp = await scanFn({ images: imagesBase64, ...(visionModel ? { visionModel } : {}) })
    return resp.data as ScanReceiptResponse
  }

  return {
    // State
    pantries,
    pantriesArray,
    pantryItemBeingEdited,

    // VueFire state
    pantriesLoading,
    pantriesError,

    // Getters
    getPantries,
    getPantriesDocs,
    tasks,
    getTopActiveItems,
    getPantry,
    getPantryMembers,
    areMultiplePantries,
    pantryPantryItems,
    getPantryPantryItems: pantryPantryItems,
    getPantryItemBeingEdited,
    getUserShoppingList,
    getPantryShoppingList,
    getIngredientIsInCurrentPantry,
    ingredientIsInCurrentPantry: getIngredientIsInCurrentPantry,

    // Actions
    setPantryItemBeingEdited,
    clearPantryItemBeingEdited,
    bindPantryMembers,
    bindPantryItems,
    bindPantryPantryItems: bindPantryItems,
    addUserToPantry,
    removeUserFromPantry,
    removePantryItemFromPantry,
    setPantryItemStatusToInStock,
    setPantryItemStatus,
    setPantryItemCategory,
    updatePantryItemQuantity,
    setPantryItemStatusToOutOfStock,
    addItemToPantry,
    addItemToCurrentPantry,
    cyclePantryItemStatus,
    savePantryItemBeingEdited,
    addIngredientAsPantryItemAlias,
    mergePantryItems,
    upsertPantry,
    addItemToUserShoppingList,
    removeItemsFromUserShoppingList,
    toggleShoppingListItemChecked,
    clearCheckedShoppingListItems,
    scanPantryPhoto,
    scanReceipt,
    unbindAllPantryData,
    editPantryItem: setPantryItemBeingEdited,
    pantry: getPantry
  }
})
